import { useEffect, useState } from "react";
import { useShallow } from "zustand/shallow";
import { USER_VERIFIED_STATUSES } from "@/features/account/types/Verification";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useAuthStore } from "../stores/authStore";
import type { Permission, PermissionReason } from "../models/permissions";

// https://puntaa.atlassian.net/browse/PKB-13744
// permissions lag a few seconds behind the firestore verification status
const DEFAULT_SETTLE_TIMEOUT_MS = 5_000;

/**
 * `isSettling` is true while the profile is verified but a permission still
 * reads VERIFICATION_STATUS_UNSUPPORTED, until it updates or times out.
 */
export const useVerifiedPermissions = <P extends Permission>(
  names: P | P[],
  timeoutMs = DEFAULT_SETTLE_TIMEOUT_MS,
): {
  permissions: Partial<Record<P, PermissionReason>>;
  isSettling: boolean;
} => {
  const keys = Array.isArray(names) ? names : [names];

  const permissions = useAuthStore(
    useShallow((state) => {
      const picked: Partial<Record<P, PermissionReason>> = {};

      if (state.status === "loading") {
        return picked;
      }

      for (const key of keys) {
        picked[key] = state.session.permissions[key];
      }

      return picked;
    }),
  );

  const { profile } = useProfile();
  const userVerificationStatus = profile?.userVerificationStatus;
  const isVerified =
    userVerificationStatus != null &&
    USER_VERIFIED_STATUSES.includes(userVerificationStatus);

  const hasStalePermission =
    isVerified &&
    keys.some((key) => permissions[key] === "VERIFICATION_STATUS_UNSUPPORTED");

  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!hasStalePermission) {
      setTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timeout);
  }, [hasStalePermission, timeoutMs]);

  return {
    permissions,
    isSettling: hasStalePermission && !timedOut,
  };
};
